import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { GerinusService } from '../lib/gerinus.service';
import { GlobalService } from '../lib/global.service';
import { Observable, of, throwError } from 'rxjs';

import Swal from 'sweetalert2';
import { IListScopeUserResponse, IUser } from 'src/app/interfaces/user';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { catchError, map } from 'rxjs/operators';
import { localStorageEnum } from 'src/app/Enums/localStorageEnum';
import { CryptoService } from './crypto.service';
import { UserSessionService } from './user-session.service';

export enum UsersGroupEnum {
    ADMIN = 1,
    EVALUATION = 2,
    CITIZEN = 3,
    DEPARTMENT_DIRECTOR = 4,
    ORGANIZATION_ADMIN = 5,
    DEPARTMENT_MEMBER = 6,
    SECRETARY = 7,
    TAX_ADMINISTRATION_COORDINATOR = 8,
}

export interface IPerfilDisponivel {
    id: number;
    nome: string;
}

@Injectable({
    providedIn: "root"
})
export class UserService extends GerinusService {
    constructor(
        public http: HttpClient,
        public global: GlobalService,
        private cryptoService: CryptoService,
        private userSession: UserSessionService,
    ) {
        super(http);
        this.entityName = 'usuario';
        this.idField = 'USR_CodigoUsuario';
        this.companyField = 'USR_CodigoEmpresa';
        this.model = 'UserModel';
        this.orderBy = 'USR_Nome';
    }

    public swal = Swal;

    public getUserById(userId: number): Observable<any> {
        const url = `${this.apiURL}/usuarios/${userId}`; // Substitua pelo endpoint real do seu backend
        return this.http.get(url);
    }

    public isAdmin(): boolean {
        return this.global.user().GRP_Descricao == 'ADMINISTRADOR';
    }

    public isTriagem(): boolean {
        return this.global.user().GRP_Descricao == 'TRIAGEM';
    }

    public isCidadao(): boolean {
        return this.global.user().GRP_Descricao == 'CIDADAO';
    }

    public isDiretorDepartamento(): boolean {
        return this.global.user().GRP_Descricao == 'DIRETOR DEPARTAMENTO';
    }

    public isMembroDepartamento(): boolean {
        return this.global.user().GRP_Descricao == 'MEMBRO DEPARTAMENTO';
    }

    public isAdmOrgao(): boolean {
        return this.global.user().GRP_Descricao == 'ADMINISTRADOR ORGAO';
    }

    public isSecretario(): boolean {
        return this.global.user().GRP_Descricao == 'SECRETARIO';
    }

    public isCommissionMember(): boolean {
        return this.global.user().USR_MembroComissao == true;
    }


    /**
     * Indica se TRIAGEM é um dos perfis vinculados ao usuário, independente de qual ele
     * esteja usando no momento. Substitui as leituras de USG_CodigoGrupoOriginal, que
     * apontavam para perfis antigos e já não valiam.
     */
    public isTriagemVinculada(): boolean {
        const usuario = this.global.user();

        if (!usuario) {
            return false;
        }

        return [usuario.USG_CodigoGrupoPrimario, usuario.USG_CodigoGrupoSecundario]
            .includes(UsersGroupEnum.EVALUATION);
    }

    public isCoordTributaria(): boolean {
        return this.global.user().GRP_Descricao == 'COORDENADOR DE ADMINISTRACAO TRIBUTARIA';
    }

    public canManageSeemDispatch(): boolean {
        return (
            this.isAdmin() ||
            this.isDiretorDepartamento() ||
            this.isSecretario() ||
            this.isMembroDepartamento() ||
            (this.isTriagem() && this.global.user().OR_Descricao == 'PROCURADORIA GERAL DO MUNICÍPIO')
        );
    }
    public formatGroupName(groupId: any = null): string {
        const formattedGropName = {
            1: 'ADMINISTRADOR',
            2: 'TRIAGEM',
            3: 'CIDADÃO',
            4: 'DIRETOR DE DEPARTAMENTO',
            5: 'ADMINISTRADOR DO ÓRGÃO',
            6: 'MEMBRO DO DEPARTAMENTO',
            7: 'SECRETÁRIO',
            8: 'COORDENADOR DE ADMINISTRAÇÃO TRIBUTÁRIA'
        };

        // O valor default de parâmetro só vale para `undefined`. Como o banco devolve `null`,
        // a normalização precisa ser explícita, senão o retorno vira `undefined` e a tela
        // exibe "Alternar para " sem nome nenhum.
        const id = groupId ?? this.global.user()?.USG_CodigoGrupo;

        return formattedGropName[id] ?? '';
    }

    /**
     * Perfis para os quais o usuário pode alternar, montados a partir do que está
     * efetivamente vinculado ao cadastro dele: grupo primário, secundário e cidadão.
     *
     * USG_CodigoGrupoOriginal é histórico ("de onde eu saí"), não permissão, e por isso
     * não entra aqui — era justamente ele que fazia a tela oferecer perfis indevidos.
     * O servidor revalida essa mesma regra; esta lista existe só para a interface.
     */
    public perfisDisponiveis(): IPerfilDisponivel[] {
        return this.derivarPerfis(this.global.user());
    }

    /**
     * Mesma regra de `perfisPermitidos` na API, aplicada sobre um cadastro qualquer.
     * Isolada para que a versão em cache e a versão atualizada não possam divergir.
     */
    private derivarPerfis(usuario: any): IPerfilDisponivel[] {
        if (!usuario) {
            return [];
        }

        const grupoAtual = Number(usuario.USG_CodigoGrupo);

        const vinculados = [
            usuario.USG_CodigoGrupoPrimario,
            usuario.USG_CodigoGrupoSecundario,
            UsersGroupEnum.CITIZEN,
        ];

        // Espelha a exceção de legado da API: cadastros anteriores à criação do grupo primário
        // não têm primário nem secundário. Sem isso, essas pessoas ficariam sem nenhum botão
        // para voltar do perfil de cidadão. Deixa de ser necessário após o backfill.
        const cadastroLegado = !usuario.USG_CodigoGrupoPrimario && !usuario.USG_CodigoGrupoSecundario;

        if (cadastroLegado && usuario.USG_CodigoGrupoOriginal) {
            vinculados.push(usuario.USG_CodigoGrupoOriginal);
        }

        // O banco devolve estas colunas ora como número, ora como string. Sem normalizar,
        // a comparação com o grupo atual falhava e a tela oferecia "alternar" para o perfil
        // que a pessoa já estava usando — que a API recusa com "já está utilizando este perfil".
        return vinculados
            .map((id) => Number(id))
            .filter((id) => !!id)
            .filter((id) => id !== grupoAtual)
            .filter((id, indice, lista) => lista.indexOf(id) === indice)
            .map((id) => ({ id, nome: this.formatGroupName(id) }));
    }

    /**
     * Perfis conferidos contra o cadastro atual, não contra o retrato gravado no login.
     *
     * `global.user()` é escrito uma vez, no login, e nunca mais recebe os vínculos de grupo.
     * Quando um administrador alterava o grupo primário/secundário de alguém já logado, a tela
     * continuava oferecendo o perfil antigo; a API valida contra o banco ao vivo e recusava.
     * Era esse o caso de "aparece a opção e não troca". Buscar o cadastro antes de montar os
     * botões faz a lista exibida coincidir com a que o servidor aceita.
     */
    /** Atualiza o telefone do próprio usuário; a pessoa é identificada pelo token na API. */
    public updateMyPhone(telefone: string): Observable<any> {
        return this.http.put(this.apiURL() + '/api/users/me/telefone', { PE_Telefone1: telefone }, this.headers());
    }

    public perfisDisponiveisAtualizados(): Observable<IPerfilDisponivel[]> {
        const usuarioLocal = this.global.user();

        if (!usuarioLocal?.USR_CodigoUsuario) {
            return of([]);
        }

        // Endpoint dedicado em vez de `getEntity`: aquele monta o cadastro inteiro em duas
        // consultas para o cliente aproveitar três códigos de grupo, e a espera acontece com a
        // tela aberta e a lista de perfis ainda vazia. Aqui é uma consulta e uma resposta curta.
        return this.http
            .get(this.apiURL() + '/api/users/perfis-disponiveis', this.headers())
            .pipe(
                map((response: any) => {
                    if (response?.grupos) {
                        this.sincronizarGrupos(response.grupos);
                    }

                    // A lista de perfis é decidida pelo servidor, pela mesma regra que valida a
                    // troca — o cliente só traduz os códigos em nomes.
                    return (response?.perfis ?? []).map((id: number) => ({
                        id: Number(id),
                        nome: this.formatGroupName(id),
                    }));
                }),
                // Indisponibilidade da API não pode esvaziar a tela: cai para o que já se sabe.
                catchError(() => of(this.derivarPerfis(usuarioLocal))),
            );
    }

    /**
     * Alinha os vínculos de grupo guardados no navegador com os do cadastro. As rotinas de
     * troca regravavam apenas o grupo em uso, então um primário/secundário desatualizado
     * permanecia errado até o próximo login.
     */
    public sincronizarGrupos(usuarioAtual: any) {
        const userData = this.global.user();

        if (!userData || !usuarioAtual) {
            return;
        }

        userData.USG_CodigoGrupo = usuarioAtual.USG_CodigoGrupo;
        userData.USG_CodigoGrupoPrimario = usuarioAtual.USG_CodigoGrupoPrimario;
        userData.USG_CodigoGrupoSecundario = usuarioAtual.USG_CodigoGrupoSecundario;
        userData.USG_CodigoGrupoOriginal = usuarioAtual.USG_CodigoGrupoOriginal;

        localStorage.setItem('user', JSON.stringify(userData));
    }

    /**
     * Indica que há uma troca de perfil em curso.
     *
     * O bloqueio é por estado, e não por tempo: uma janela fixa erraria sempre que a resposta
     * fosse mais lenta que ela, e travaria o botão à toa quando fosse mais rápida. Aqui o botão
     * volta exatamente quando a operação termina.
     *
     * Importa mais do que parece porque `changeUserGroup` é um alternador: dois cliques não
     * repetem a mesma troca, eles vão e voltam — a pessoa terminaria no perfil de origem
     * achando que trocou.
     */
    public trocandoPerfil = false;

    handleChangeInternalUserGroup(rememberChoice: boolean) {
        if (this.trocandoPerfil) {
            return;
        }

        this.trocandoPerfil = true;

        // O grupo de destino não é mais enviado: quem decide é o servidor, a partir dos
        // perfis vinculados ao usuário. Aqui vai apenas a intenção de alternar.
        const usuario = this.global.user();

        const userRequest = {
            type: 'changeUserGroup',
            USR_LembrarEscolha: rememberChoice,
            // USG_CodigoUsuario só existe porque o login devolve o join com usuario_grupo.
            // O identificador próprio do usuário é o primeiro a valer.
            USR_CodigoUsuario: usuario?.USR_CodigoUsuario ?? usuario?.USG_CodigoUsuario,
        };

        this.save(userRequest).subscribe({
            next: (
                response: any) => {
                if (!response?.ok) {
                    this.trocandoPerfil = false;
                    Swal.fire({
                        title: 'Não foi possível alternar',
                        text: response?.message ?? 'Tente novamente mais tarde.',
                        icon: 'warning',
                    });
                    return;
                }

                // Continua bloqueado no caminho de sucesso: `aplicarTrocaLocal` navega e a tela
                // é remontada, então liberar aqui só reabriria a janela para um clique extra.
                this.aplicarTrocaLocal(response.entity);
            },
            // Sem este tratamento, qualquer 4xx/5xx virava um clique sem reação nenhuma na
            // tela — o usuário via o botão, clicava e nada acontecia.
            error: (err: any) => {
                this.trocandoPerfil = false;
                Swal.fire({
                    title: 'Não foi possível alternar',
                    text: err?.error?.message ?? 'Tente novamente mais tarde.',
                    icon: 'warning',
                });
            },
        });
    }

    /**
     * Grava o perfil novo e recoloca a aplicação em estado coerente sem recarregar a página.
     *
     * Antes isto terminava em `window.location.reload()`, que refazia o bootstrap inteiro do
     * Angular — o item mais caro da troca de perfil. Não era necessário: a pessoa é a mesma, só
     * o grupo muda. O que precisa acompanhar a mudança são o menu e as telas, e ambos se
     * resolvem com uma navegação mais o aviso para quem vive fora do `router-outlet`.
     *
     * O aviso de sucesso também deixou de exigir clique: antes a espera do usuário fechar o
     * modal entrava inteira no tempo percebido de troca.
     */
    public aplicarTrocaLocal(userResponse: any) {
        this.sincronizarGrupos(userResponse);

        const userData = this.global.user();

        userData.USR_Administrador = userResponse.USR_Administrador;
        userData.GRP_CodigoGrupo = userResponse.USG_CodigoGrupo;
        userData.GRP_Descricao = userResponse.GRP_Descricao;
        userData.USR_LembrarEscolha = userResponse.USR_LembrarEscolha;

        localStorage.setItem('user', JSON.stringify(userData));

        this.userSession.notificarPerfilAlterado();

        // A home é remontada do zero porque o painel muda conforme o perfil. `replaceUrl` evita
        // que o botão voltar traga de volta uma tela renderizada sob o perfil anterior.
        this.global.router.navigate(['/inicio'], { replaceUrl: true });

        Swal.fire({
            title: 'Grupo de usuário Alterado',
            text: `Seu acesso agora é como ${this.formatGroupName(userResponse.USG_CodigoGrupo)}`,
            icon: 'success',
            showConfirmButton: false,
            showCancelButton: false,
            timer: 1500,
            timerProgressBar: true,
        });
    }

    public getUser(): IUser {
        return JSON.parse(localStorage.getItem('user')) as IUser
    }

    public rememberChoice(userId: number) {
        return this.http.put(this.apiURL() + `/api/users/${userId}/remember-choice`, { USR_LembrarEscolha: true }, this.headers());
    }

    public generateListPDF(title: string, condition = null) {
        const doc = new jsPDF('p', 'mm', 'a4');
        doc.setFont('Inter-SemiBold');
        doc.text(title, doc.internal.pageSize.width / 2 || doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });
        this.listAll(condition, 0).subscribe({
            next: (response: IListScopeUserResponse) => {

                if (response.data && response.data.length > 0) {

                    const tableData = [];

                    for (let item of response.data) {
                        tableData.push([item.PE_Nome, item.GRP_Descricao, item.OR_Descricao, item.DEP_Descricao, item.USR_Vinculo]);
                    }

                    const tableConfig = {
                        startY: 20,
                        margin: { top: 20 },
                        styles: {
                            fontSize: 10,
                        },
                        headStyles: {
                            fontSize: 8,
                        },
                        bodyStyles: {
                            fontSize: 8,
                        },
                    };

                    autoTable(doc, {
                        head: [['NOME', 'GRUPO DE USUÁRIO', 'ÓRGÃO', 'DEPARTAMENTO', 'VÍNCULO'],],
                        body: tableData,
                        ...tableConfig,
                        styles : {
                            valign: 'middle'
                        }
                    });

                    doc.save("Lista de Usuários.pdf");
                }
            }
        })
    }

    toggleToSecondaryUserGroup(userId: number, groupChange: number): Observable<any> {
        return this.http.put(this.apiURL() + '/api/users/' + userId + '/toggle-to-secondary-group/' + groupChange, {}, this.headers())
        .pipe(
            catchError(this.handleError)
        )
    }
    
    getAccountLevel(): string {
        const accountEncrypt = localStorage.getItem(localStorageEnum.ACCOUNT_GOVBR);

        if (accountEncrypt) {
            try {
                const accountDecryptAndParse: any = JSON.parse(this.cryptoService.decrypt(accountEncrypt));

                return accountDecryptAndParse.id;
            } catch {
                // Conta salva com a chave antiga do CryptoService: descarta, o próximo login grava de novo.
                localStorage.removeItem(localStorageEnum.ACCOUNT_GOVBR);
            }
        }

        return null;
    }

    updateRegistrationUser(pessoaId: any,data: any) {
        return this.http.put(this.apiURL() + '/api/users/' + pessoaId + '/update-registration/', data, this.headers())
    }

    private handleError(error: any): Observable<never> {
        console.error('An error occurred:', error);
        // A mensagem do servidor precisa chegar à tela: é ela que explica ao usuário por que
        // o perfil foi recusado. Antes, tudo virava um texto genérico em inglês.
        const mensagem = error?.error?.message ?? 'Não foi possível concluir a operação. Tente novamente.';
        return throwError(() => new Error(mensagem));
    }

    public generateListReportPDF(title: string, condition: string) {
        const formData = new FormData();
        formData.append('title', title);
        formData.append('condition', condition);

        return this.http.post(
            this.apiURL() + '/api/pdf/user-report-list', 
            formData, 
            { 
                ...this.headers(true), 
                responseType: 'blob', 
                reportProgress: true, 
                observe: 'events' 
            }
        );
    }

    public gerarSenhaTemporaria(userId: number) {
        return this.http.post(this.apiURL() + `/api/users/${userId}/gerar-senha-temporaria`, {}, this.headers());
    }

    public definirSenha(token: string, novaSenha: string) {
        return this.http.post(this.apiURL() + '/api/users/definir-senha', { token, novaSenha }, this.headers());
    }

    public enviarCodigoConfirmacao(token: string) {
        return this.http.post<{ emailMascarado: string }>(this.apiURL() + '/api/users/enviar-codigo', { token }, this.headers());
    }

    public validarCodigoConfirmacao(token: string, codigo: string) {
        return this.http.post(this.apiURL() + '/api/users/validar-codigo', { token, codigo }, this.headers());
    }
}
